import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { 
  Sparkles, 
  LogOut, 
  Calendar, 
  DollarSign, 
  Clock,
  CheckCircle,
  Car,
  User,
  Phone,
  Mail,
  MapPin,
  Trash2,
  Eye,
  CalendarOff,
  Plus
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format, addDays, startOfWeek, endOfWeek, addWeeks } from "date-fns";
import { formatTimeDisplay, parseBookingDate } from "@/utils/dateUtils";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filter, setFilter] = useState('all');
  const [blockedDates, setBlockedDates] = useState([]);
  const [blockingDate, setBlockingDate] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    
    const fetchDashboardData = async () => {
      try {
        const authHeader = { headers: { Authorization: `Bearer ${token}` } };
        const [bookingsRes, statsRes, blockedRes] = await Promise.all([
          axios.get(`${API}/admin/bookings`, authHeader),
          axios.get(`${API}/admin/stats`, authHeader),
          axios.get(`${API}/admin/blocked-dates`, authHeader)
        ]);
        setBookings(bookingsRes.data);
        setStats(statsRes.data);
        setBlockedDates(blockedRes.data.blocked_dates || []);
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem('admin_token');
          navigate('/admin');
          toast.error("Session expired. Please login again.");
        } else {
          toast.error("Failed to load data");
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, [navigate]);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }
  });

  const fetchData = async () => {
    try {
      const [bookingsRes, statsRes, blockedRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`, getAuthHeader()),
        axios.get(`${API}/admin/stats`, getAuthHeader()),
        axios.get(`${API}/admin/blocked-dates`, getAuthHeader())
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
      setBlockedDates(blockedRes.data.blocked_dates || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin');
        toast.error("Session expired. Please login again.");
      } else {
        toast.error("Failed to load data");
      }
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (bookingId, newStatus) => {
    try {
      await axios.patch(
        `${API}/admin/bookings/${bookingId}/status`,
        { status: newStatus },
        getAuthHeader()
      );
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, getAuthHeader());
      toast.success("Booking deleted");
      fetchData();
    } catch (err) {
      toast.error("Failed to delete booking");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin');
  };

  // Block next week helper
  const blockNextWeek = async () => {
    setBlockingDate(true);
    try {
      const today = new Date();
      const nextMonday = startOfWeek(addWeeks(today, 1), { weekStartsOn: 1 });
      const nextSunday = endOfWeek(nextMonday, { weekStartsOn: 1 });
      
      await axios.post(
        `${API}/admin/blocked-dates/range`,
        {
          start_date: format(nextMonday, 'yyyy-MM-dd'),
          end_date: format(nextSunday, 'yyyy-MM-dd'),
          reason: "Away - No bookings"
        },
        getAuthHeader()
      );
      toast.success("Next week blocked successfully!");
      fetchData();
    } catch (err) {
      toast.error("Failed to block dates");
    } finally {
      setBlockingDate(false);
    }
  };

  // Unblock a specific date
  const unblockDate = async (date) => {
    try {
      await axios.delete(`${API}/admin/blocked-dates/${date}`, getAuthHeader());
      toast.success("Date unblocked");
      fetchData();
    } catch (err) {
      toast.error("Failed to unblock date");
    }
  };

  // Unblock all dates
  const unblockAll = async () => {
    if (!window.confirm("Are you sure you want to unblock all dates?")) return;
    try {
      for (const blocked of blockedDates) {
        await axios.delete(`${API}/admin/blocked-dates/${blocked.date}`, getAuthHeader());
      }
      toast.success("All dates unblocked");
      fetchData();
    } catch (err) {
      toast.error("Failed to unblock dates");
    }
  };

  // Use imported formatTimeDisplay from utils
  const formatTime = formatTimeDisplay;

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'status-pending',
      confirmed: 'status-confirmed',
      completed: 'status-completed',
      cancelled: 'status-cancelled'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const filteredBookings = bookings.filter(b => 
    filter === 'all' ? true : b.status === filter
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-slate-900 border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-sky-400" />
                <span className="logo-text text-lg text-white">RISE & SHINE</span>
              </Link>
              <span className="text-gray-500">|</span>
              <span className="text-gray-400">Admin Dashboard</span>
            </div>
            <Button 
              variant="ghost" 
              onClick={handleLogout}
              className="text-gray-400 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <Card className="bg-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-sky-500/10 rounded-lg">
                    <Calendar className="w-6 h-6 text-sky-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Bookings</p>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-total">
                      {stats.total_bookings}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-yellow-500/10 rounded-lg">
                    <Clock className="w-6 h-6 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Pending</p>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-pending">
                      {stats.pending}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-500/10 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Completed</p>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-completed">
                      {stats.completed}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-emerald-500/10 rounded-lg">
                    <DollarSign className="w-6 h-6 text-emerald-500" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Revenue</p>
                    <p className="text-2xl font-bold text-foreground" data-testid="stat-revenue">
                      ${stats.total_revenue}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Blocked Dates Section */}
        <Card className="bg-card mb-8">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold flex items-center gap-2">
              <CalendarOff className="w-5 h-5 text-red-500" />
              Blocked Dates
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={blockNextWeek}
                disabled={blockingDate}
                className="bg-red-500 hover:bg-red-600"
                data-testid="block-next-week-btn"
              >
                {blockingDate ? "Blocking..." : "Block Next Week"}
              </Button>
              {blockedDates.length > 0 && (
                <Button
                  variant="outline"
                  onClick={unblockAll}
                  data-testid="unblock-all-btn"
                >
                  Unblock All
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {blockedDates.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">No dates are currently blocked</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {blockedDates.sort((a, b) => a.date.localeCompare(b.date)).map((blocked) => (
                  <div 
                    key={blocked.date}
                    className="flex items-center gap-2 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded-lg text-sm"
                    data-testid={`blocked-${blocked.date}`}
                  >
                    <CalendarOff className="w-4 h-4 text-red-500" />
                    <span className="font-medium">{format(new Date(blocked.date + 'T12:00:00'), 'EEE, MMM d')}</span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-500/20"
                      onClick={() => unblockDate(blocked.date)}
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Bookings Section */}
        <Card className="bg-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl font-bold">Bookings</CardTitle>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-40" data-testid="filter-select">
                <SelectValue placeholder="Filter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="confirmed">Confirmed</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {filteredBookings.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No bookings found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBookings.map((booking) => (
                  <div 
                    key={booking.id}
                    className="flex flex-col lg:flex-row lg:items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors gap-4"
                    data-testid={`booking-${booking.id}`}
                  >
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-semibold text-foreground">
                          {booking.client.name}
                        </span>
                        {getStatusBadge(booking.status)}
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Sparkles className="w-4 h-4" />
                          {booking.service_name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(booking.date), 'MMM d, yyyy')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatTime(booking.time_slot)}
                        </span>
                        <span className="font-medium text-sky-500">
                          ${booking.service_price}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* View Details Dialog */}
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => setSelectedBooking(booking)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader>
                            <DialogTitle>Booking Details</DialogTitle>
                          </DialogHeader>
                          {selectedBooking && (
                            <div className="space-y-4">
                              <div>
                                <p className="text-sm text-muted-foreground">Invoice #</p>
                                <p className="font-medium">{selectedBooking.invoice_number}</p>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm text-muted-foreground">Service</p>
                                  <p className="font-medium">{selectedBooking.service_name}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Price</p>
                                  <p className="font-medium text-sky-500">${selectedBooking.service_price}</p>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <p className="text-sm text-muted-foreground">Date</p>
                                  <p className="font-medium">{format(new Date(selectedBooking.date), 'MMM d, yyyy')}</p>
                                </div>
                                <div>
                                  <p className="text-sm text-muted-foreground">Time</p>
                                  <p className="font-medium">{formatTime(selectedBooking.time_slot)}</p>
                                </div>
                              </div>
                              <div className="pt-4 border-t">
                                <p className="text-sm text-muted-foreground mb-2">Client</p>
                                <div className="space-y-2">
                                  <p className="flex items-center gap-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    {selectedBooking.client.name}
                                  </p>
                                  <p className="flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-muted-foreground" />
                                    {selectedBooking.client.email}
                                  </p>
                                  <p className="flex items-center gap-2">
                                    <Phone className="w-4 h-4 text-muted-foreground" />
                                    {selectedBooking.client.phone}
                                  </p>
                                  <p className="flex items-center gap-2">
                                    <MapPin className="w-4 h-4 text-muted-foreground" />
                                    {selectedBooking.client.address}
                                  </p>
                                </div>
                              </div>
                              <div className="pt-4 border-t">
                                <p className="text-sm text-muted-foreground mb-2">Vehicle</p>
                                <p className="flex items-center gap-2">
                                  <Car className="w-4 h-4 text-muted-foreground" />
                                  {selectedBooking.vehicle.make} {selectedBooking.vehicle.model}
                                  {selectedBooking.vehicle.color && ` (${selectedBooking.vehicle.color})`}
                                </p>
                                <p className="text-sm text-muted-foreground mt-1">
                                  License: {selectedBooking.vehicle.license_plate}
                                </p>
                              </div>
                              {selectedBooking.special_requests && (
                                <div className="pt-4 border-t">
                                  <p className="text-sm text-muted-foreground mb-2">Special Requests</p>
                                  <p className="text-sm bg-muted p-3 rounded">{selectedBooking.special_requests}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </DialogContent>
                      </Dialog>

                      {/* Status Dropdown */}
                      <Select 
                        value={booking.status}
                        onValueChange={(value) => updateStatus(booking.id, value)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>

                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => deleteBooking(booking.id)}
                        data-testid={`delete-${booking.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
